import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import { ArrowRight, MessageSquare, FileText, Image as ImageIcon } from 'lucide-react';
import { ImageAnalysis } from '../types';
import AnalysisSummary from './AnalysisSummary';
import ImageModal from './ImageModal';

interface Props {
  prompts: string[];
  onSelectPrompt: (index: number) => void;
}

const extractQuestionType = (question: string): string | null => {
  // Match everything before the colon
  const match = question.match(/^([^:]+):/);
  return match ? match[1] : null;
};

const formatQuestion = (question: string): string => {
  // Remove everything before and including the colon, then trim
  return question.replace(/^[^:]+:\s*/, '').trim();
};

export default function QuestionsPage() {
  const navigate = useNavigate();
  const { imageAnalysis, setCurrentQuestionIndex } = useAppContext();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  if (!imageAnalysis) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <p className="text-gray-600">No analysis available. Please upload content first.</p>
        </div>
      </div>
    );
  }

  const isTextAnalysis = !imageAnalysis.imageUrl && 'text' in imageAnalysis;

  const handleSelectPrompt = (index: number) => {
    setCurrentQuestionIndex(index);
    navigate('/recording');
    // Use a small delay to ensure navigation completes
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Angle</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select a question that resonates with your expertise and create a thought-provoking response.
        </p>
      </div>


            {/* Analysis Summary */}
            <AnalysisSummary 
        imageAnalysis={imageAnalysis} 
        onOpenImage={() => setIsImageModalOpen(true)} 
      />

      {/* Questions Grid */}
      <div className="grid gap-6">
        {imageAnalysis.questions.map((question, index) => {
          const questionType = extractQuestionType(question);
          const formattedQuestion = formatQuestion(question);
          
          return (
            <button
              key={index}
              onClick={() => handleSelectPrompt(index)}
              className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative p-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-block text-sm font-medium text-blue-600">
                        Question {index + 1} of {imageAnalysis.questions.length}
                      </span>
                      {questionType && (
                        <span className="inline-block px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                          {questionType}
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {formattedQuestion}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 self-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Image Modal */}
      {!isTextAnalysis && (
        <ImageModal 
          imageUrl={imageAnalysis.imageUrl} 
          isOpen={isImageModalOpen} 
          onClose={() => setIsImageModalOpen(false)} 
        />
      )}
    </div>
  );
}
